import React, { useEffect, useMemo, useState } from "react";
import ApiURL from "../controllers/Api";
import CompanyLayout from "./CompanyLayout";
import EmployeeLayout from "./EmployeeLayout";
import { toast } from "../utils/toast";

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem("token");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const createRequirementDraft = () => ({
  id: null,
  name: "",
  start_date: "",
  end_date: "",
});

const emptyForm = {
  id: null,
  name: "",
  contact_name: "",
  contact_email: "",
  notes: "",
  requirements: [createRequirementDraft()],
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
};

export default function CompanySubCompanies() {
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();
  const isEmployeeView = storedUser?.user_type === "employee" || storedUser?.role === "employee";
  const Layout = isEmployeeView ? EmployeeLayout : CompanyLayout;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [relatedModal, setRelatedModal] = useState({
    open: false,
    loading: false,
    saving: false,
    requirement: null,
    resumes: [],
    selectedResumeIds: [],
  });
  const [credentialsModal, setCredentialsModal] = useState({
    open: false,
    loginEmail: "",
    password: "",
    subCompanyName: "",
  });

  const [resetPwModal, setResetPwModal] = useState({
    open: false,
    subCompanyId: null,
    subCompanyName: "",
    loading: false,
  });

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${ApiURL}crm/company/get_sub_companies.php`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to load sub companies");
      }
      setItems(result?.data?.sub_companies || []);
    } catch (error) {
      console.error("Error loading sub companies:", error);
      toast.error(error.message || "Failed to load sub companies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const relatedSelectionSet = useMemo(
    () => new Set(relatedModal.selectedResumeIds.map(String)),
    [relatedModal.selectedResumeIds]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRequirementChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      requirements: prev.requirements.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addRequirementField = () => {
    setForm((prev) => ({
      ...prev,
      requirements: [...prev.requirements, createRequirementDraft()],
    }));
  };

  const removeRequirementField = (index) => {
    setForm((prev) => {
      const nextRequirements = prev.requirements.filter((_, itemIndex) => itemIndex !== index);
      return {
        ...prev,
        requirements: nextRequirements.length > 0 ? nextRequirements : [createRequirementDraft()],
      };
    });
  };

  const openCreateForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowCreateForm(true);
  };

  const closeCreateForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowCreateForm(false);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      id: item.id,
      name: item.name || "",
      contact_name: item.contact_name || "",
      contact_email: item.contact_email || "",
      notes: item.notes || "",
      requirements: item.requirements?.length
        ? item.requirements.map((requirement) => ({
            id: requirement.id || null,
            name: requirement.name || "",
            start_date: requirement.start_date || "",
            end_date: requirement.end_date || "",
          }))
        : [createRequirementDraft()],
    });
    setShowCreateForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${ApiURL}crm/company/create_sub_company.php`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          ...form,
          requirements: form.requirements
            .map((item) => ({
              id: item.id || null,
              name: item.name.trim(),
              start_date: item.start_date || "",
              end_date: item.end_date || "",
            }))
            .filter((item) => item.name),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to save sub company");
      }

      toast.success(editingId ? "Sub company updated successfully." : "Sub company created successfully.");
      closeCreateForm();
      await loadItems();
      
      // Show credentials modal if credentials were generated
      if (!editingId && result.data?.credentials) {
        setCredentialsModal({
          open: true,
          loginEmail: result.data.credentials.login_email,
          password: result.data.credentials.password,
          subCompanyName: form.name,
        });
      }
    } catch (error) {
      console.error("Error saving sub company:", error);
      toast.error(error.message || "Failed to save sub company");
    } finally {
      setSaving(false);
    }
  };

  const openRelatedResumes = async (subCompany, requirement) => {
    setRelatedModal({
      open: true,
      loading: true,
      saving: false,
      requirement: {
        ...requirement,
        sub_company_id: subCompany.id,
        sub_company_name: subCompany.name,
      },
      resumes: [],
      selectedResumeIds: [],
    });

    try {
      const response = await fetch(
        `${ApiURL}crm/company/get_related_resumes.php?requirement_id=${requirement.id}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to load related resumes");
      }

      const resumes = result?.data?.resumes || [];
      setRelatedModal((prev) => ({
        ...prev,
        loading: false,
        requirement: result?.data?.requirement || prev.requirement,
        resumes,
        selectedResumeIds: resumes.filter((item) => item.already_tagged).map((item) => item.id),
      }));
    } catch (error) {
      console.error("Error loading related resumes:", error);
      toast.error(error.message || "Failed to load related resumes");
      setRelatedModal((prev) => ({ ...prev, loading: false, resumes: [], selectedResumeIds: [] }));
    }
  };

  const closeRelatedModal = () => {
    setRelatedModal({
      open: false,
      loading: false,
      saving: false,
      requirement: null,
      resumes: [],
      selectedResumeIds: [],
    });
  };

  const closeCredentialsModal = () => {
    setCredentialsModal({
      open: false,
      loginEmail: "",
      password: "",
      subCompanyName: "",
    });
  };

  const resetPassword = async (item) => {
    setResetPwModal({
      open: true,
      subCompanyId: item.id,
      subCompanyName: item.name,
      loading: false,
    });
  };

  const handleResetPwConfirm = async () => {
    const { subCompanyId, loading } = resetPwModal;
    if (loading) return;

    setResetPwModal(prev => ({ ...prev, loading: true }));

    try {
      const response = await fetch(`${ApiURL}crm/company/reset_password.php`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ sub_company_id: subCompanyId }),
      });
      const result = await response.json();

      if (response.ok && result.success) {
        toast.success('Password reset for ' + resetPwModal.subCompanyName);
        setCredentialsModal({
          open: true,
          loginEmail: result.data.credentials.login_email,
          password: result.data.credentials.password,
          subCompanyName: resetPwModal.subCompanyName,
        });
        setResetPwModal({ open: false, subCompanyId: null, subCompanyName: '', loading: false });
        await loadItems(); // Refresh list
      } else {
        toast.error(result.message || 'Reset failed');
      }
    } catch (error) {
      toast.error('Request failed');
    } finally {
      setResetPwModal(prev => ({ ...prev, loading: false }));
    }
  };

  const closeResetPwModal = () => {
    setResetPwModal({ open: false, subCompanyId: null, subCompanyName: '', loading: false });
  };

  const handleBulkTag = async () => {
    if (!relatedModal.requirement || relatedModal.selectedResumeIds.length === 0) {
      toast.warning("Please select at least one resume");
      return;
    }

    setRelatedModal((prev) => ({ ...prev, saving: true }));
    try {
      const response = await fetch(`${ApiURL}crm/company/bulk_tag_requirement.php`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          requirement_id: relatedModal.requirement.id,
          resume_ids: relatedModal.selectedResumeIds,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to tag related resumes");
      }

      toast.success(result.message || "Related resumes tagged successfully.");
      closeRelatedModal();
    } catch (error) {
      console.error("Error tagging related resumes:", error);
      toast.error(error.message || "Failed to tag related resumes");
      setRelatedModal((prev) => ({ ...prev, saving: false }));
    }
  };

  return (
    <Layout
      title="Sub Companies"
      description="Manage company targets, requirement dates, and quickly tag related resumes."
      headerAction={
        !showCreateForm ? (
          <button
            type="button"
            onClick={openCreateForm}
            className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Add Sub Company
          </button>
        ) : null
      }
    >
      <div className="space-y-4">
        {showCreateForm ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {editingId ? "Edit Sub Company" : "Add Sub Company"}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Add one or more requirements with a valid from and to date.
                </p>
              </div>
              <button
                type="button"
                onClick={closeCreateForm}
                className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close sub company form"
              >
                x
              </button>
            </div>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Sub Company Name</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                    placeholder="e.g. Wipro"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Contact Name</label>
                  <input
                    name="contact_name"
                    value={form.contact_name}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Contact Email</label>
                  <input
                    name="contact_email"
                    type="text"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    value={form.contact_email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label className="block text-sm font-medium text-slate-700">Requirements</label>
                  <button
                    type="button"
                    onClick={addRequirementField}
                    className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                  >
                    Add Field
                  </button>
                </div>
                <div className="space-y-3">
                  {form.requirements.map((requirement, index) => (
                    <div
                      key={requirement.id ? `req-${requirement.id}` : `${editingId || "new"}-${index}`}
                      className="grid gap-3 rounded-xl border border-gray-200 p-3 md:grid-cols-[1.6fr_1fr_1fr_auto]"
                    >
                      <input
                        value={requirement.name}
                        onChange={(event) => handleRequirementChange(index, "name", event.target.value)}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                        placeholder={`Requirement ${index + 1}`}
                      />
                      <input
                        type="date"
                        value={requirement.start_date}
                        onChange={(event) => handleRequirementChange(index, "start_date", event.target.value)}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                      />
                      <input
                        type="date"
                        value={requirement.end_date}
                        onChange={(event) => handleRequirementChange(index, "end_date", event.target.value)}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeRequirementField(index)}
                        disabled={form.requirements.length === 1}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Notes</label>
                <textarea
                  name="notes"
                  rows={4}
                  value={form.notes}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Optional notes"
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {saving ? "Saving..." : editingId ? "Update Sub Company" : "Create Sub Company"}
                </button>
              </div>
            </form>
          </div>
        ) : null}

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left font-medium text-slate-600">Sub Company</th>
                  <th className="px-3 py-3 text-left font-medium text-slate-600">Contact</th>
                  <th className="px-3 py-3 text-left font-medium text-slate-600">Login ID</th>
                  <th className="px-3 py-3 text-left font-medium text-slate-600">Requirements</th>
                  <th className="px-3 py-3 text-left font-medium text-slate-600">Notes</th>
                  <th className="px-3 py-3 text-left font-medium text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 align-top">
                    <td className="px-3 py-3 font-medium text-slate-900">{item.name}</td>
                    <td className="px-3 py-3 text-slate-600">
                      <div>{item.contact_name || "-"}</div>
                      <div className="text-[10px] text-slate-500">{item.contact_email || ""}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-600">
                      <div className="font-medium text-slate-900">{item.login_email || "-"}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-500 bg-gray-100 px-2 py-1 rounded-full">●●●●●●●●</span>
                        <button
                          onClick={() => resetPassword(item)}
                          className="text-xs bg-orange-500 text-white px-3 py-1 rounded-full hover:bg-orange-600"
                        >
                          Reset
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-slate-600 min-w-[360px]">
                      {item.requirements?.length ? (
                        <div className="space-y-2">
                          {item.requirements.map((requirement) => (
                            <div
                              key={requirement.id}
                              className="flex flex-col gap-2 rounded-xl border border-gray-200 px-3 py-2 md:flex-row md:items-center md:justify-between"
                            >
                              <div>
                                <div className="font-medium text-slate-900">{requirement.name}</div>
                                <div className="text-[10px] text-slate-500">
                                  {formatDate(requirement.start_date)} to {formatDate(requirement.end_date)}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => openRelatedResumes(item, requirement)}
                                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                              >
                                Show Related Resumes
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-3 py-3 text-slate-600">{item.notes || "-"}</td>
                    <td className="px-3 py-3">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 flex-1"
                        >
                          Edit
                        </button>
                        <a 
                          href={`${ApiURL}crm/company/export_tagged_resumes.php?sub_company_id=${item.id}&token=${encodeURIComponent(localStorage.getItem("token") || "")}`}
                          className="rounded-lg border border-gray-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100 flex-1 text-center"
                        >
                          CSV Export
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                      No sub companies created yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {relatedModal.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 backdrop-blur-[1px]">
          <div className="w-full max-w-5xl overflow-hidden rounded-[28px] border border-gray-200 bg-white text-gray-900 shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
              <div>
                <h3 className="text-2xl font-semibold tracking-tight text-gray-900">Related Resumes</h3>
                <p className="mt-2 text-sm text-gray-500">
                  {relatedModal.requirement?.sub_company_name} / {relatedModal.requirement?.name}
                </p>
                <p className="text-xs text-gray-400">
                  {formatDate(relatedModal.requirement?.start_date)} to {formatDate(relatedModal.requirement?.end_date)}
                </p>
              </div>
              <button
                type="button"
                onClick={closeRelatedModal}
                className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                x
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setRelatedModal((prev) => ({
                      ...prev,
                      selectedResumeIds: prev.resumes.map((item) => item.id),
                    }))
                  }
                  className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setRelatedModal((prev) => ({
                      ...prev,
                      selectedResumeIds: [],
                    }))
                  }
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                >
                  Clear
                </button>
                <span className="text-xs text-gray-500">Selected: {relatedModal.selectedResumeIds.length}</span>
              </div>

              <div className="max-h-[420px] overflow-auto rounded-2xl border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">Select</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">Resume</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">Candidate</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">Role</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">Category</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">ATS</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">State</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {relatedModal.loading ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                          Loading related resumes...
                        </td>
                      </tr>
                    ) : relatedModal.resumes.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                          No related resumes found.
                        </td>
                      </tr>
                    ) : (
                      relatedModal.resumes.map((resume) => (
                        <tr key={resume.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              checked={relatedSelectionSet.has(String(resume.id))}
                              onChange={(event) =>
                                setRelatedModal((prev) => ({
                                  ...prev,
                                  selectedResumeIds: event.target.checked
                                    ? [...prev.selectedResumeIds, resume.id]
                                    : prev.selectedResumeIds.filter((item) => item !== resume.id),
                                }))
                              }
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-3 py-3 text-slate-600">
                            <div className="font-medium text-slate-900">#{resume.id}</div>
                            <div className="text-[10px] text-slate-500">{resume.original_filename}</div>
                          </td>
                          <td className="px-3 py-3 text-slate-600">
                            <div>{resume.candidate_name || "-"}</div>
                            <div className="text-[10px] text-slate-500">{resume.candidate_email || ""}</div>
                          </td>
                          <td className="px-3 py-3 text-slate-600">{resume.candidate_role || "-"}</td>
                          <td className="px-3 py-3 text-slate-600">{resume.category || "-"}</td>
                          <td className="px-3 py-3 text-slate-600">{resume.ats_score || 0}%</td>
                          <td className="px-3 py-3">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-medium ${
                                resume.already_tagged ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {resume.already_tagged ? "Already Tagged" : "New Match"}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-5">
              <button
                type="button"
                onClick={closeRelatedModal}
                className="rounded-2xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkTag}
                disabled={relatedModal.saving}
                className="rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {relatedModal.saving ? "Tagging..." : "Tag Selected Resumes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Credentials Modal */}
{credentialsModal.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-5">
              <h3 className="text-lg font-semibold text-gray-900">Sub-Company Login Credentials</h3>
              <p className="mt-1 text-sm text-gray-600">
                Credentials for <span className="font-medium">{credentialsModal.subCompanyName}</span>
              </p>
            </div>
            <div className="px-6 py-5">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Login Email</label>
                  <div className="mt-1.5 flex">
                    <input
                      type="text"
                      readOnly
                      value={credentialsModal.loginEmail}
                      className="flex-1 rounded-l-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(credentialsModal.loginEmail);
                        toast.success("Email copied to clipboard");
                      }}
                      className="rounded-r-lg border border-l-0 border-gray-300 bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <div className="mt-1.5 flex">
                    <input
                      type="text"
                      readOnly
                      value={credentialsModal.password}
                      className="flex-1 rounded-l-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(credentialsModal.password);
                        toast.success("Password copied to clipboard");
                      }}
                      className="rounded-r-lg border border-l-0 border-gray-300 bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <div className="rounded-lg bg-blue-50 p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Important:</strong> Share these credentials with the sub-company. They can use them to log in at the CRM login page.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-5">
              <button
                type="button"
                onClick={closeCredentialsModal}
                className="rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {resetPwModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-5">
              <h3 className="text-lg font-semibold text-gray-900">
                Reset Password for {resetPwModal.subCompanyName}
              </h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Generate new password? Current users will need to use the new one.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={closeResetPwModal}
                  disabled={resetPwModal.loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPwConfirm}
                  disabled={resetPwModal.loading}
                  className="px-4 py-2 text-sm font-semibold text-white bg-orange-500 rounded-xl hover:bg-orange-600 disabled:opacity-50"
                >
                  {resetPwModal.loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
